import React, { useEffect, useRef, useCallback, useState } from 'react';
import * as fabric from 'fabric';
import { Stroke, Tool, Recording } from '../../types';
import { RecordingEvent } from '../../types/recording';
import { usePlayerStore, usePlaybackState } from './playerStore';
import { createFabricObject, animateStrokeDrawing } from './canvasUtils';
import { denormalizeRecordingEvent } from '../../utils/normalizedCoordinates';
import { ActionProcessor } from './ActionProcessor';
import { ViewportCulling } from './ViewportCulling';
import { calculateQuestionPositionForPlayback } from '../../utils/questionPositioning';
import { getQuestionImageUrl } from '../../utils/questionUtils';
import { PlaybackTimingController } from './PlaybackTimingController';

interface CanvasPlayerProps {
  className?: string;
}

const CanvasPlayerComponent: React.FC<CanvasPlayerProps> = ({ className = '' }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const fabricCanvasRef = useRef<fabric.Canvas | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const lastProcessedTimeRef = useRef<number>(0);
  const currentEventIndexRef = useRef<number>(0);
  const startTimeRef = useRef<number>(0);
  const pausedAtRef = useRef<number>(0);
  const actionProcessorRef = useRef<ActionProcessor | null>(null);
  const viewportCullingRef = useRef<ViewportCulling | null>(null);
  const [scale, setScale] = useState(1);
  const lastContainerSizeRef = useRef({ width: 0, height: 0 });
  const timingControllerRef = useRef(PlaybackTimingController.getInstance());
  const syncCallbackUnregisterRef = useRef<(() => void) | null>(null);
  
  const { recording, events, setCanvasSize, duration, updateCurrentTime, pause, hasAudioError, isSeeking } = usePlayerStore() as { 
    recording: Recording | null; 
    events: RecordingEvent[]; 
    setCanvasSize: any;
    duration: number;
    updateCurrentTime: (time: number) => void;
    pause: () => void;
    hasAudioError: boolean;
    isSeeking: boolean;
  };
  const { currentTime, isPlaying, playbackSpeed } = usePlaybackState();
  
  // Get canvas dimensions from recording
  const canvasWidth = recording?.canvasSize?.width || 1920;
  const canvasHeight = recording?.canvasSize?.height || 1080;
  
  // Initialize canvas
  useEffect(() => {
    if (!canvasRef.current || fabricCanvasRef.current) return;
    
    
    const canvas = new fabric.Canvas(canvasRef.current, {
      backgroundColor: 'white',
      renderOnAddRemove: true,
      selection: false,
      preserveObjectStacking: true,
      width: canvasWidth,
      height: canvasHeight,
      skipOffscreen: false // Ensure all objects are rendered even if off-screen initially
    });
    
    fabricCanvasRef.current = canvas;
    actionProcessorRef.current = new ActionProcessor(canvas);
    viewportCullingRef.current = new ViewportCulling(canvas);
    
    // Register timing controller sync callback
    const unregister = timingControllerRef.current.registerSyncCallback((syncedTime) => {
      // Update canvas time in timing controller
      timingControllerRef.current.updateCanvasTime(syncedTime);
    });
    
    syncCallbackUnregisterRef.current = unregister;
    
    // Ensure canvas is visible
    canvas.backgroundColor = 'white';
    canvas.requestRenderAll();
    
    // Set up canvas size
    const updateCanvasSize = () => {
      if (!containerRef.current || !canvas) return;
      
      // Check if canvas is properly initialized
      if (!canvas.lowerCanvasEl || !canvas.upperCanvasEl) {
        setTimeout(updateCanvasSize, 50);
        return;
      }
      
      const container = containerRef.current;
      
      
      // Get the actual container size - use clientWidth/Height for more reliable results
      const width = container.clientWidth;
      const height = container.clientHeight;
      
      // Ensure we have valid dimensions
      if (width <= 0 || height <= 0) {
        setTimeout(updateCanvasSize, 100);
        return;
      }
      
      // Check if size actually changed
      if (lastContainerSizeRef.current.width === width && 
          lastContainerSizeRef.current.height === height) {
        return; // No size change, skip update
      }
      
      lastContainerSizeRef.current = { width, height };
      
      // Get container dimensions without padding (full screen)
      const containerWidth = Math.max(100, width);
      const containerHeight = Math.max(100, height);
      
      // Calculate scale to fit while maintaining aspect ratio
      const scaleX = containerWidth / canvasWidth;
      const scaleY = containerHeight / canvasHeight;
      let newScale = Math.min(scaleX, scaleY);
      
      // Apply min/max scale limits to prevent extreme scaling
      const minScale = 0.1;
      const maxScale = 2.0;
      newScale = Math.max(minScale, Math.min(maxScale, newScale));
      
      // Only log significant updates
      if (Math.abs(newScale - scale) > 0.01) {
      }
      
      try {
        // YENI YAKLAŞIM: Canvas'ı scale etme, sadece CSS transform kullan
        // Canvas'ı orijinal boyutunda bırak
        canvas.setZoom(1);
        canvas.absolutePan(new fabric.Point(0, 0));
        
        setScale(newScale);
        setCanvasSize(canvasWidth, canvasHeight, newScale);
        
        // Render
        canvas.requestRenderAll();
      } catch (error) {
        // Retry after a short delay
        setTimeout(updateCanvasSize, 100);
      }
    };
    
    // Debounce resize handler
    let resizeTimeout: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updateCanvasSize, 100);
    };
    
    // Initial scale calculation with retry
    const initializeScale = () => {
      // Ensure canvas is ready
      if (!canvas.lowerCanvasEl || !canvas.upperCanvasEl) {
        setTimeout(initializeScale, 50);
        return;
      }
      
      updateCanvasSize();
      // Retry if container dimensions are not ready
      if (containerRef.current) {
        const { clientWidth, clientHeight } = containerRef.current;
        if (clientWidth === 0 || clientHeight === 0) {
          setTimeout(initializeScale, 50);
        }
      }
    };
    
    // Wait for canvas to be fully ready before sizing
    setTimeout(() => {
      requestAnimationFrame(() => {
        initializeScale();
      });
    }, 0);
    
    window.addEventListener('resize', handleResize);
    
    // Also observe container size changes
    const resizeObserver = new ResizeObserver((entries) => {
      // Only handle resize if dimensions actually changed
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (width > 0 && height > 0) {
          handleResize();
        }
      }
    });
    
    // Observe the container ref
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    
    return () => {
      clearTimeout(resizeTimeout);
      window.removeEventListener('resize', handleResize);
      resizeObserver.disconnect();
      if (syncCallbackUnregisterRef.current) {
        syncCallbackUnregisterRef.current();
        syncCallbackUnregisterRef.current = null;
      }
      if (actionProcessorRef.current) {
        actionProcessorRef.current.reset();
      }
      canvas.dispose();
      fabricCanvasRef.current = null;
      actionProcessorRef.current = null;
      viewportCullingRef.current = null;
    };
  }, [canvasWidth, canvasHeight, setCanvasSize]);
  
  // Process events based on current time
  const processEvents = useCallback((targetTime: number) => {
    const canvas = fabricCanvasRef.current;
    if (!canvas || !events.length) return;
    
    // Handle backward seeking with tolerance
    const timeDiff = targetTime - lastProcessedTimeRef.current;
    
    if (timeDiff < -100) { // Only handle as backward seek if > 100ms difference
      
      // Clear render times so strokes can be re-rendered from the correct point
      if (actionProcessorRef.current) {
        (actionProcessorRef.current as any).clearRenderTimes();
      }
      
      // Find the correct index for the target time
      let targetIndex = 0;
      for (let i = 0; i < events.length; i++) {
        if (events[i].timestamp > targetTime) {
          targetIndex = i;
          break;
        }
      }
      
      // If we're going significantly backward (> 5 seconds), reset and replay
      if (timeDiff < -5000 || currentEventIndexRef.current - targetIndex > 100) {
        // Full reset for large jumps
        if (actionProcessorRef.current) {
          actionProcessorRef.current.reset();
        }
        currentEventIndexRef.current = 0;
        
        // Replay all events up to target in larger batches for better performance
        const BATCH_SIZE = 30; // Increased batch size for backward seeks
        const eventsToProcess: RecordingEvent[] = [];
        
        for (let i = 0; i < targetIndex; i++) {
          const event = events[i];
          const denormalizedEvent = denormalizeRecordingEvent(
            event as any,
            canvasWidth,
            canvasHeight
          );
          
          eventsToProcess.push(denormalizedEvent as RecordingEvent);
          
          // Process batch when it reaches BATCH_SIZE
          if (eventsToProcess.length >= BATCH_SIZE) {
            if (actionProcessorRef.current) {
              actionProcessorRef.current.processBatch(eventsToProcess);
            }
            eventsToProcess.length = 0; // Clear array
          }
        }
        
        // Process remaining events
        if (eventsToProcess.length > 0 && actionProcessorRef.current) {
          actionProcessorRef.current.processBatch(eventsToProcess);
        }
        currentEventIndexRef.current = targetIndex;
      } else {
        // For small backward seeks, just update the index
        currentEventIndexRef.current = targetIndex;
      }
    } else {
      // Forward playback - process events progressively with adaptive batching
      const BATCH_SIZE = isPlaying ? 20 : 10; // Larger batches during playback
      const eventsToProcess: RecordingEvent[] = [];
      
      // Collect events to process
      while (currentEventIndexRef.current < events.length) {
        const event = events[currentEventIndexRef.current];
        
        // Only process events that should have happened by now
        if (event.timestamp > targetTime) {
          break;
        }
        
        // Denormalize event coordinates
        const denormalizedEvent = denormalizeRecordingEvent(
          event as any,
          canvasWidth,
          canvasHeight
        );
        
        eventsToProcess.push(denormalizedEvent as RecordingEvent);
        currentEventIndexRef.current++;
        
        // Process batch when it reaches BATCH_SIZE
        if (eventsToProcess.length >= BATCH_SIZE) {
          if (actionProcessorRef.current) {
            actionProcessorRef.current.processBatch(eventsToProcess);
          }
          eventsToProcess.length = 0; // Clear array
        }
      }
      
      // Process remaining events
      if (eventsToProcess.length > 0 && actionProcessorRef.current) {
        if (eventsToProcess.length === 1) {
          actionProcessorRef.current.processEvent(eventsToProcess[0]);
        } else {
          actionProcessorRef.current.processBatch(eventsToProcess);
        }
      }
      
      // Silent - no need to log event counts
    }
    
    // Always update stroke animations based on current time
    if (actionProcessorRef.current) {
      (actionProcessorRef.current as any).updateAnimations(targetTime);
    }
    
    lastProcessedTimeRef.current = targetTime;
  }, [events, canvasWidth, canvasHeight, isPlaying]);
  
  // Animation support for specific strokes
  const handleAnimatedStroke = useCallback((event: RecordingEvent) => {
    if (event.type !== 'add_stroke' || !fabricCanvasRef.current) return;
    
    const stroke = event.data.stroke;
    const fabricObject = createFabricObject({
      ...stroke,
      tool: stroke.tool as Tool
    });
    
    if (fabricObject && ['pen', 'pencil', 'highlighter'].includes(stroke.tool)) {
      // Get the object from ActionProcessor and apply animation
      const strokeMap = actionProcessorRef.current?.getStrokeMap();
      if (strokeMap?.has(stroke.id)) {
        const existingObject = strokeMap.get(stroke.id);
        if (existingObject) {
          fabricCanvasRef.current.remove(existingObject);
          animateStrokeDrawing(fabricObject, {...stroke, tool: stroke.tool as Tool}, fabricCanvasRef.current);
          strokeMap.set(stroke.id, fabricObject);
        }
      }
    }
  }, []);
  
  // Handle seeking and process events based on time changes - audio drives timing
  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas || !events.length) return;
    
    // Skip processing if we're at time 0 and haven't processed anything yet
    // This prevents the "Seeking backward" log on initial load
    if (currentTime === 0 && lastProcessedTimeRef.current === 0 && currentEventIndexRef.current === 0) {
      return;
    }
    
    // Process events based on current time (audio-driven)
    processEvents(currentTime);
    
    // Only log occasionally to reduce noise
    if (isPlaying && Math.floor(currentTime / 1000) !== Math.floor(lastProcessedTimeRef.current / 1000)) {
      // Performance monitoring
      const diagnostics = timingControllerRef.current.getDiagnostics();
      if (diagnostics.drift > 100) {
        console.warn(`High drift detected: ${diagnostics.drift}ms`);
      }
    }
  }, [currentTime, events, processEvents, isPlaying]);
  
  // Animation loop for smooth playback (only when no audio)
  useEffect(() => {
    const { audioElement } = usePlayerStore.getState();
    const hasWorkingAudio = audioElement && audioElement.src && !audioElement.error && !hasAudioError;
    
    // If we have working audio, let AudioSync drive the timing
    if (hasWorkingAudio) {
      // Audio is handling timing, we don't need our own animation loop
      return;
    }
    
    
    if (!isPlaying) {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      // Store pause time
      if (startTimeRef.current > 0) {
        pausedAtRef.current = currentTime;
      }
      return;
    }
    
    // Resume from paused position
    startTimeRef.current = performance.now() - (pausedAtRef.current || currentTime) / playbackSpeed;
    
    const animate = () => {
      if (!isPlaying) return;
      
      // Calculate elapsed time with high precision
      const elapsed = (performance.now() - startTimeRef.current) * playbackSpeed;
      
      // Update store time
      if (elapsed <= duration) {
        updateCurrentTime(elapsed);
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        // Playback complete
        pause();
        updateCurrentTime(duration);
      }
    };
    
    animate();
    
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isPlaying, playbackSpeed, duration, currentTime, pause, updateCurrentTime, hasAudioError]);
  
  // Load question and parent images if needed
  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas || !recording) return;
    
    // Prevent multiple loads
    const existingQuestionImage = canvas.getObjects().find(obj => (obj as any).name === 'questionBackground');
    if (existingQuestionImage) {
      return;
    }
    
    const loadImages = async () => {
      let parentImg: fabric.Image | null = null;
      let parentImageHeight = 0;
      
      // Load parent image first if exists
      if (recording.parentImageUrl) {
        try {
          const parentUrl = getQuestionImageUrl(recording.parentImageUrl, true);
          if (parentUrl) {
            // Load image using Image constructor to avoid CORS issues
            parentImg = await new Promise<fabric.Image | null>((resolve) => {
              const image = new Image();
              image.onload = () => {
                const fabricImg = new fabric.Image(image);
                parentImageHeight = fabricImg.height;
                fabricImg.set({ name: 'parentImage' });
                resolve(fabricImg);
              };
              image.onerror = () => {
                console.error('Failed to load parent image');
                resolve(null);
              };
              image.src = parentUrl;
            });
          }
        } catch (error) {
          console.error('Failed to load parent image:', error);
        }
      }
      
      // Load question image
      if (recording.questionImageUrl) {
        try {
          const questionUrl = getQuestionImageUrl(recording.questionImageUrl, true);
          if (questionUrl) {
            // Load image using Image constructor to avoid CORS issues
            const questionImg = await new Promise<fabric.Image | null>((resolve) => {
              const image = new Image();
              image.onload = () => {
                const fabricImg = new fabric.Image(image);
                fabricImg.set({ name: 'questionImage' });
                resolve(fabricImg);
              };
              image.onerror = () => {
                console.error('Failed to load question image');
                resolve(null);
              };
              image.src = questionUrl;
            });
            
            if (questionImg && questionImg.width && questionImg.height) {
            // BASIT YAKLAŞIM
            // 1. Canvas zoom'u kesinlikle 1'de tut (zoom yokken yerleştir)
            canvas.setZoom(1);
            canvas.absolutePan(new fabric.Point(0, 0));
            
            // 2. Saved position varsa direkt kullan
            const savedPos = recording.questionMetadata?.position;
            if (savedPos && savedPos.left !== undefined && savedPos.top !== undefined) {
              
              // Parent image varsa ekle
              if (parentImg && parentImageHeight > 0) {
                const parentScale = savedPos.scale * (questionImg.width / (parentImg.width || 1));
                parentImg.set({
                  left: savedPos.left,
                  top: savedPos.top,
                  scaleX: parentScale,
                  scaleY: parentScale,
                  selectable: false,
                  evented: false
                });
                canvas.add(parentImg);
                canvas.sendObjectToBack(parentImg);
              }
              
              // Question image ekle
              let questionTop = savedPos.top;
              if (parentImg && parentImageHeight > 0) {
                const parentScale = savedPos.scale * (questionImg.width / (parentImg.width || 1));
                questionTop = savedPos.top + (parentImageHeight * parentScale) + 20;
              }
              
              questionImg.set({
                left: savedPos.left,
                top: questionTop,
                scaleX: savedPos.scale,
                scaleY: savedPos.scale,
                selectable: false,
                evented: false
              });
              canvas.add(questionImg);
              canvas.sendObjectToBack(questionImg);
              
            } else {
              // Saved position yoksa default kullan
              questionImg.set({
                left: 40,
                top: 40,
                scaleX: 0.5,
                scaleY: 0.5,
                selectable: false,
                evented: false
              });
              canvas.add(questionImg);
              canvas.sendObjectToBack(questionImg);
            }
            
            // 3. Canvas'ı render et
            canvas.requestRenderAll();
            
            // Debug için canvas bounds kontrolü
            const bounds = questionImg.getBoundingRect();
            }
          }
        } catch (error) {
          console.error('Failed to load question image:', error);
        }
      }
    };
    
    loadImages();
  }, [recording?.id, recording?.questionImageUrl, recording?.parentImageUrl]);
  
  // Ensure question images are loaded before processing events
  useEffect(() => {
    if (!fabricCanvasRef.current || !recording) return;
    
    // Reset canvas when recording changes
    const canvas = fabricCanvasRef.current;
    
    // Find and preserve question images
    const questionImages = canvas.getObjects().filter(obj => {
      const objName = (obj as any).name;
      return objName === 'questionImage' || objName === 'parentImage';
    });
    
    // Remove all objects except question images
    const objects = canvas.getObjects().filter(obj => {
      const objName = (obj as any).name;
      return objName !== 'questionImage' && objName !== 'parentImage';
    });
    
    objects.forEach(obj => canvas.remove(obj));
    canvas.backgroundColor = 'white';
    
    // Ensure question images are at the back
    questionImages.forEach(img => {
      canvas.sendObjectToBack(img);
    });
    
    // Reset event processing
    if (actionProcessorRef.current) {
      actionProcessorRef.current.reset();
    }
    currentEventIndexRef.current = 0;
    lastProcessedTimeRef.current = 0;
    
    console.log('Canvas reset, question images preserved:', questionImages.length);
    
    // Process initial events at time 0 to show the initial state
    // This ensures the canvas displays content even when paused at start
    if (events.length > 0) {
      processEvents(0);
    }
  }, [recording, events, processEvents]);
  
  return (
    <div ref={containerRef} className="absolute inset-0 flex items-center justify-center">
      <div 
        className="bg-white relative"
        style={{
          width: `${canvasWidth}px`,
          height: `${canvasHeight}px`,
          transform: `scale(${scale})`,
          transformOrigin: 'center'
        }}
      >
        <canvas 
          ref={canvasRef}
          style={{ display: 'block' }}
        />
      </div>
    </div>
  );
};

export const CanvasPlayer = React.memo(CanvasPlayerComponent);